import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray, noop} from 'ramda-adjunct';

import {useBatchDeleteSeriesMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['name'] as const;

export function SeriesDefinition() {
  const navigate = useNavigate();

  const [batchDeleteSeries] = useBatchDeleteSeriesMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const ids = isArray(rowId) ? rowId : [rowId];

    const name = getColumnData<string>('name')?.value;

    match(actionKey)
      .with('detail', () => {
        navigate(composePath(settingsRoutes.seriesDefinitionEdit, {params: {id: ids[0]}}));
      })
      .with('delete', () => {
        openDeleteDialog({
          name,
          onConfirm: () =>
            batchDeleteSeries({body: {items: ids.map((id) => ({id}))}})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .otherwise(noop);
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.document.labels.seriesTitle'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.document.actions.newSeries'),
        onClick: () => navigate(settingsRoutes.seriesDefinitionNew),
        'data-testid': testIds.settings.seriesDefinition('new'),
      },
    ],
  };

  return (
    <SettingsTemplate header={header} data-testid={testIds.settings.seriesDefinition('template')}>
      <DmsDataGrid
        gridCode="series-definition"
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        data-testid={testIds.settings.seriesDefinition('grid')}
      />
    </SettingsTemplate>
  );
}
